import { ReactElement, ReactNode } from "react";
import FormRow from "../UI/FormRow";
import HeaderMessage from "../UI/HeaderMessage";
import Comments from "../fields/Comments";
import ComputeSave from "../fields/ComputeSave";
import ControlledAutoComplete from "../fields/ControlledAutoComplete";
import Currency from "../fields/Currency";
import Email from "../fields/Email";
import FileUpload from "../fields/FileUpload";
import HTMLLink from "../fields/HTMLLink";
import Hidden from "../fields/Hidden";
import PPButton from "../fields/PPButton";
import PPCheckbox from "../fields/PPCheckbox";
import PPDatePicker from "../fields/PPDatePicker";
import PPRadioGroup, { ChoiceValue } from "../fields/PPRadioGroup";
import PPTextField from "../fields/PPTextField";
import PPTimePicker from "../fields/PPTimePicker";
import PhoneInputField from "../fields/PhoneInputField";
import SmartAddressInput from "../fields/SmartAddressInput";
import ZipCode from "../fields/ZipCode";
import {
  FieldConfiguration,
  FormPropertiesConfiguration,
  FormRowConfiguration,
  LookUpOptionsConfiguration,
  PPComponent,
  SectionConfiguration,
} from "./form-configuration";

export const getSectionByFieldId = (
  form: FormPropertiesConfiguration,
  fieldId: string
): SectionConfiguration | undefined => {
  const sections: SectionConfiguration[] = form.sections;
  return sections.find((section: SectionConfiguration) => {
    const fields: FieldConfiguration[] | undefined = section.fields;
    if (fields) {
      return (
        fields.find((field: FieldConfiguration) => field.id == fieldId) !==
        undefined
      );
    }
  });
};

const bgColor: string = "#ffffff";

const isFieldFullRow = (field: FieldConfiguration): boolean => {

  if (
    field &&
    (field.field_type === "message" ||
      field.field_type === "boldmessage" ||
      field.field_type === "blkboldmsg" ||
      field.field_type === "textarea" ||
      field.field_type === "hidden" ||
      field.field_type === "heading")
  ) {
    return true;
  }
  return false;
};

const createRow = (
  index: number,
  fields: Array<FieldConfiguration>
): {
  numFields: number;
  formRowFields: Array<FieldConfiguration | ReactElement>;
} => {
  const formRowFields: Array<FieldConfiguration | ReactElement> = [];
  for (let i = 0; i < 4; i++) {
    <span key={"span-row" + index + "col" + i} />;
  }

  let numFields = 0;
  for (let j = 0; j < 4; j++) {
    if (fields[index + j] && !isFieldFullRow(fields[index + j])) {
      formRowFields[j] = fields[index + j];
      numFields++;
    } else break;
  }

  return { numFields, formRowFields };
};

const createLayoutFields = (
  fields: Array<FieldConfiguration> | undefined,
  section: SectionConfiguration
): Array<FormRowConfiguration | FieldConfiguration> => {
  const layoutFields: Array<FormRowConfiguration | FieldConfiguration> = [];
  if (fields !== undefined) {
    const sortedFields = [...fields].sort((a, b) => a.order - b.order);

    for (let i = 0; i < sortedFields.length; i++) {
      if (isFieldFullRow(sortedFields[i])) {
        layoutFields.push(sortedFields[i]);
      } else {
        const { numFields, formRowFields } = createRow(i, sortedFields);
        if (numFields > 0) {
          const type: string = "FormRow";
          const formRow: FormRowConfiguration = {
            section: section,
            fields: formRowFields,
            field_type: type,
          };
          layoutFields.push(formRow);
          i += numFields - 1;
        }
      }
    }
  }

  return layoutFields;
};

export const isSectionTypeView = (section: SectionConfiguration): boolean => {
  return section.type === "v";
};

const containsValidationType = (
  validationType: string,
  validationString: string | undefined
): boolean => {
  let ret: boolean = false;
  if (validationString) {
    const tokens: string[] | undefined = validationString.split(",");
    if (tokens) {
      ret =
        tokens.filter((token: string) => token === validationType).length == 1;
    } else {
      ret = validationType === validationString;
    }
  }
  return ret;
};

export const componentConfiguration = (
  section: SectionConfiguration
): Array<FormRowConfiguration | FieldConfiguration> => {
  let config: Array<FormRowConfiguration | FieldConfiguration>;

  if (isSectionTypeView(section)) {
    const fieldsOfView = section.forms![0].sections![0].fields;
    config = createLayoutFields(fieldsOfView, section);
  } else {
    config = createLayoutFields(section.fields, section);
  }
  return config;
};

export const singleFieldRow = (field: FieldConfiguration): PPComponent => {
  let component: PPComponent = {
    node: undefined,
    configuration: undefined,
  };
  if (
    field.field_type === "boldmessage" ||
    field.field_type === "blkboldmsg" ||
    field.field_type === "message" ||
    field.field_type === "heading"
  ) {
    component.node = (
      <HeaderMessage
        key={field.id}
        extendedAttributes={field.extendedAttributes ?? {}}
        message={field.label}
      />
    );
  }
  if (field.field_type === "textarea") {
    component.node = (
      <Comments
        key={field.id}
        id={field.id}
        label={field.label}
        bgColor={bgColor}
        length={field.length}
        helperText={field.help_text ?? ""}
        required={containsValidationType("required", field.validationType)}
        extendedAttributes={field.extendedAttributes ?? {}}
        extendedEvents={field.extendedEvents}
      />
    );
  }
  if (field.field_type === "hidden") {
    component.node = (
      <Hidden key={field.id} id={field.id} defaultValue={field.default_value} />
    );
  }

  component.configuration = field;

  return component;
};

export const buildRow = (
  field: FormRowConfiguration | FieldConfiguration,
  index: number
): PPComponent => {
  let component: PPComponent;
  if (field && field.field_type !== "FormRow") {
    component = singleFieldRow(field as FieldConfiguration);
  } else {
    component = formRow(field as FormRowConfiguration, index);
  }
  return component;
};

export const formRow = (
  formRowConfig: FormRowConfiguration,
  index: number
): PPComponent => {


  let component: PPComponent = {
    node: undefined,
    configuration: undefined,
  };
  component.node = (
    <FormRow
      key={"formRow-" + formRowConfig.section.name + "-" + index}
      configuration={formRowConfig}
    >
      {formRowConfig.fields.map((currentField) => {
        let innerComp: ReactNode;
        const reactElement: ReactElement = currentField as ReactElement;
        const fieldConfiguration: FieldConfiguration =
          currentField as FieldConfiguration;



        if (reactElement.type === "span") {
          innerComp = currentField as ReactElement<
            any,
            string | React.JSXElementConstructor<any>
          >;
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "button"
        ) {
          innerComp = (
            <PPButton
              key={fieldConfiguration.id}
              props={{
                id: fieldConfiguration.id,
                label: fieldConfiguration.label,
                helperText: fieldConfiguration.help_text ?? "",
                required: false,
                extendedAttributes: fieldConfiguration.extendedAttributes ?? {},
                extendedEvents: fieldConfiguration.extendedEvents,
              }}
            ></PPButton>
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "htmllink"
        ) {
          innerComp = (
            <HTMLLink
              key={fieldConfiguration.id}
              value={fieldConfiguration.default_value?.split(" ")[0]}
              href={fieldConfiguration.default_value?.split(" ")[3]}
              label={fieldConfiguration.label}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "spacer2col"
        ) {
          innerComp = <span key={fieldConfiguration.id} />;
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "radio"
        ) {
          let thisOptions: ChoiceValue[] = [];
          if (fieldConfiguration.lookup_options) {
            thisOptions = fieldConfiguration.lookup_options.map((option) => {
              const choice: ChoiceValue = {
                label: option.value,
                value: option.id,
              } as ChoiceValue;
              return choice;
            });
          }
          innerComp = (
            <PPRadioGroup
              {...formRowConfig}
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              choices={thisOptions}
              helperText={fieldConfiguration.help_text ?? ""}
              defaultValue={fieldConfiguration.default_value}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "text"
        ) {
          innerComp = (
            <PPTextField
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              length={fieldConfiguration.length}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "computeSave"
        ) {
          innerComp = (
            <ComputeSave
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              value={fieldConfiguration.default_value}
              length={fieldConfiguration.length}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "file"
        ) {
          innerComp = (
            <FileUpload
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "checkbox"
        ) {
          innerComp = (
            <PPCheckbox
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "combo"
        ) {
          const emptyOption: LookUpOptionsConfiguration = {
            id: "",
            value: "",
          };
          innerComp = (
            <ControlledAutoComplete
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              options={fieldConfiguration.lookup_options!}
              multiple={false}
              label={fieldConfiguration.label}
              defaultValue={emptyOption}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        if (
          (fieldConfiguration.field_type &&
            (fieldConfiguration.field_type === "listbox" ||
              fieldConfiguration.field_type === "multicheckbox")) ||
          fieldConfiguration.field_type === "multiselect"
        ) {
          const defaultValue: Array<LookUpOptionsConfiguration> = [];
          innerComp = (
            <ControlledAutoComplete
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              options={fieldConfiguration.lookup_options!}
              multiple={true}
              label={fieldConfiguration.label}
              defaultValue={defaultValue}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "address"
        ) {
          innerComp = (
            <SmartAddressInput
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "phone"
        ) {

          innerComp = (
            <PhoneInputField
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "email"
        ) {
          innerComp = (
            <Email
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
              length={fieldConfiguration.length}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "zip"
        ) {
          innerComp = (
            <ZipCode
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "time"
        ) {
          innerComp = (
            <PPTimePicker
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "date"
        ) {
          innerComp = (
            <PPDatePicker
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        if (
          fieldConfiguration.field_type &&
          fieldConfiguration.field_type === "currency"
        ) {
          innerComp = (
            <Currency
              key={fieldConfiguration.id}
              id={fieldConfiguration.id}
              label={fieldConfiguration.label}
              length={fieldConfiguration.length}
              helperText={fieldConfiguration.help_text ?? ""}
              required={containsValidationType(
                "required",
                fieldConfiguration.validationType
              )}
              extendedAttributes={fieldConfiguration.extendedAttributes ?? {}}
              extendedEvents={fieldConfiguration.extendedEvents}
            />
          );
        }

        return innerComp;
      })}
    </FormRow>
  );

  component.configuration = formRowConfig;

  return component;
};
